/**
 * Device settings only (no AI chat history, agent session, or other ephemeral state).
 * Chat uses cursorAgent.messages / agentId / pendingRun — intentionally omitted.
 */
export const APP_LOCAL_STORAGE_BACKUP_KEYS = [
  'dailylife_translate_settings',
  'cursorAgent.preferences',
  'cursorAgent.repoUrl',
  'netlify.deploy.preferences',
  'lichess.preferences',
  'dailylife_github_auth',
  'dailylife_pat',
  'earTraining.prefs',
  'sidebar_collapsed',
] as const;

export type AppLocalStorageBackupKey = (typeof APP_LOCAL_STORAGE_BACKUP_KEYS)[number];

export const APP_LOCAL_STORAGE_BACKUP_FORMAT = 'dailylife-localStorage-backup';
export const APP_LOCAL_STORAGE_BACKUP_VERSION = 1;

export type AppLocalStorageBackup = {
  format: typeof APP_LOCAL_STORAGE_BACKUP_FORMAT;
  version: number;
  exportedAt: string;
  entries: Partial<Record<AppLocalStorageBackupKey, string>>;
};

export type ApplyBackupMode = 'merge' | 'replace';

export type ApplyBackupResult = {
  applied: AppLocalStorageBackupKey[];
  removed: AppLocalStorageBackupKey[];
};

const KEY_SET = new Set<string>(APP_LOCAL_STORAGE_BACKUP_KEYS);

function isBackupKey(key: string): key is AppLocalStorageBackupKey {
  return KEY_SET.has(key);
}

/** Collect current values for all known app keys (omits missing keys). */
export function exportAppLocalStorageBackup(now: Date = new Date()): AppLocalStorageBackup {
  const entries: Partial<Record<AppLocalStorageBackupKey, string>> = {};
  for (const key of APP_LOCAL_STORAGE_BACKUP_KEYS) {
    try {
      const value = localStorage.getItem(key);
      if (value !== null) entries[key] = value;
    } catch {
      /* ignore quota / private mode */
    }
  }
  return {
    format: APP_LOCAL_STORAGE_BACKUP_FORMAT,
    version: APP_LOCAL_STORAGE_BACKUP_VERSION,
    exportedAt: now.toISOString(),
    entries,
  };
}

export function serializeAppLocalStorageBackup(backup: AppLocalStorageBackup): string {
  return JSON.stringify(backup, null, 2);
}

export class AppLocalStorageBackupError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AppLocalStorageBackupError';
  }
}

function parseEntriesObject(raw: unknown): Partial<Record<AppLocalStorageBackupKey, string>> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new AppLocalStorageBackupError('Backup must include an "entries" object.');
  }
  const entries: Partial<Record<AppLocalStorageBackupKey, string>> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!isBackupKey(key)) continue;
    if (typeof value !== 'string') {
      throw new AppLocalStorageBackupError(`Entry "${key}" must be a string.`);
    }
    entries[key] = value;
  }
  if (Object.keys(entries).length === 0) {
    throw new AppLocalStorageBackupError('No recognized settings keys in backup.');
  }
  return entries;
}

/** Parse exported JSON (official format or a flat map of known keys). */
export function parseAppLocalStorageBackup(text: string): AppLocalStorageBackup {
  const trimmed = text.trim();
  if (!trimmed) throw new AppLocalStorageBackupError('Paste a backup JSON blob first.');

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    throw new AppLocalStorageBackupError('Invalid JSON — check for trailing commas or truncated paste.');
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new AppLocalStorageBackupError('Backup must be a JSON object.');
  }

  const obj = parsed as Record<string, unknown>;

  if (obj.format === APP_LOCAL_STORAGE_BACKUP_FORMAT) {
    const version = obj.version;
    if (typeof version !== 'number' || version > APP_LOCAL_STORAGE_BACKUP_VERSION) {
      throw new AppLocalStorageBackupError(
        `Unsupported backup version (${String(version)}). Update the app and try again.`
      );
    }
    const exportedAt =
      typeof obj.exportedAt === 'string' && obj.exportedAt.trim()
        ? obj.exportedAt
        : new Date().toISOString();
    const entries = parseEntriesObject(obj.entries);
    return {
      format: APP_LOCAL_STORAGE_BACKUP_FORMAT,
      version: APP_LOCAL_STORAGE_BACKUP_VERSION,
      exportedAt,
      entries,
    };
  }

  // Flat map: { "dailylife_translate_settings": "{...}", ... }
  const entries = parseEntriesObject(obj);
  return {
    format: APP_LOCAL_STORAGE_BACKUP_FORMAT,
    version: APP_LOCAL_STORAGE_BACKUP_VERSION,
    exportedAt: new Date().toISOString(),
    entries,
  };
}

export function applyAppLocalStorageBackup(
  backup: AppLocalStorageBackup,
  mode: ApplyBackupMode = 'merge'
): ApplyBackupResult {
  const applied: AppLocalStorageBackupKey[] = [];
  const removed: AppLocalStorageBackupKey[] = [];

  if (mode === 'replace') {
    for (const key of APP_LOCAL_STORAGE_BACKUP_KEYS) {
      if (key in backup.entries) continue;
      try {
        if (localStorage.getItem(key) !== null) {
          localStorage.removeItem(key);
          removed.push(key);
        }
      } catch {
        /* ignore */
      }
    }
  }

  for (const key of APP_LOCAL_STORAGE_BACKUP_KEYS) {
    const value = backup.entries[key];
    if (value === undefined) continue;
    try {
      localStorage.setItem(key, value);
      applied.push(key);
    } catch {
      throw new AppLocalStorageBackupError(
        `Could not write "${key}" — storage may be full or disabled.`
      );
    }
  }

  if (applied.length === 0) {
    throw new AppLocalStorageBackupError('Nothing to apply — backup has no matching keys.');
  }

  return { applied, removed };
}

export function summarizeBackupKeys(keys: readonly AppLocalStorageBackupKey[]): string {
  const labels: Record<AppLocalStorageBackupKey, string> = {
    dailylife_translate_settings: 'Translation',
    'cursorAgent.preferences': 'AI Agent',
    'cursorAgent.repoUrl': 'AI Agent repo (legacy)',
    'netlify.deploy.preferences': 'Netlify deploy',
    'lichess.preferences': 'Lichess',
    dailylife_github_auth: 'GitHub sign-in',
    dailylife_pat: 'GitHub PAT (legacy)',
    'earTraining.prefs': 'Ear training',
    sidebar_collapsed: 'Sidebar collapsed',
  };
  return keys.map(k => labels[k] ?? k).join(', ');
}
